import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Trash2, Inbox, ArrowLeft, Plus, Star, Upload, ImageIcon, Search, Download, KeyRound, Save, Eye, EyeOff, BarChart3 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = ["new", "contacted", "quoted", "closed"];
const statusColor = {
  new: "bg-brand-orange text-white",
  contacted: "bg-blue-500/20 text-blue-300 border border-blue-500/40",
  quoted: "bg-brand-green/20 text-brand-green border border-brand-green/40",
  closed: "bg-white/10 text-white/50 border border-white/20",
};

const fieldCls = "bg-transparent border-white/20 text-white h-11 focus-visible:ring-brand-orange";

function PasswordInput({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        data-testid={testid}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${fieldCls} pr-11`}
      />
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-brand-orange transition-colors"
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

function LoginView() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-ink px-6">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
        <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-white/50 hover:text-brand-orange transition-colors mb-8 text-sm">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="bg-black/50 backdrop-blur-xl border border-white/10 p-8 md:p-10">
          <div className="mb-6">
            <img src="/clarkes-logomark.png" alt="Clarkes GroundCare" className="h-11 w-auto" />
          </div>
          <h1 className="font-heading text-white text-3xl mb-8 mt-4">Admin login</h1>
          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-widest">Email</Label>
              <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls} placeholder="admin@…" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-widest">Password</Label>
              <PasswordInput testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {error && <p data-testid="admin-login-error" className="text-red-400 text-sm">{error}</p>}
            <button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 bg-brand-orange text-white font-semibold hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
              {loading && <Loader2 size={18} className="animate-spin" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function QuotesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data);
    } catch (e) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
    try {
      const { data } = await api.get("/stats");
      setStats(data);
    } catch (e) { /* stats are non-critical */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: quotes.filter((q) => q.status === s).length }), {});

  const term = search.trim().toLowerCase();
  const filtered = quotes.filter((q) => {
    const matchesStatus = statusFilter === "all" || q.status === statusFilter;
    const matchesSearch =
      !term ||
      [q.name, q.email, q.phone, q.service, q.address, q.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(term));
    return matchesStatus && matchesSearch;
  });

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("Nothing to export"); return; }
    const headers = ["Name", "Phone", "Email", "Address", "Service", "Status", "Message", "Submitted"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((q) => [q.name, q.phone, q.email, q.address, q.service, q.status, q.message, new Date(q.created_at).toLocaleString("en-GB")].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clarkes-quotes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} quote${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <div>
      {stats && (
        <div className="mb-6" data-testid="views-stats">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={16} className="text-brand-orange" />
            <span className="text-xs uppercase tracking-widest text-white/50">Website views <span className="text-white/30 normal-case tracking-normal">(only visible to you)</span></span>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-2xl">
            <div data-testid="views-total" className="border border-white/10 bg-brand-surface p-4">
              <p className="font-display text-brand-orange text-3xl">{stats.total}</p>
              <p className="text-white/50 text-xs uppercase tracking-widest mt-1">Total</p>
            </div>
            <div data-testid="views-last7" className="border border-white/10 bg-brand-surface p-4">
              <p className="font-display text-white text-3xl">{stats.last7}</p>
              <p className="text-white/50 text-xs uppercase tracking-widest mt-1">Last 7 days</p>
            </div>
            <div data-testid="views-today" className="border border-white/10 bg-brand-surface p-4">
              <p className="font-display text-white text-3xl">{stats.today}</p>
              <p className="text-white/50 text-xs uppercase tracking-widest mt-1">Today</p>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1 max-w-3xl">
          {STATUSES.map((s) => (
            <div key={s} data-testid={`stat-${s}`} className="border border-white/10 bg-brand-surface p-4">
              <p className="font-display text-white text-3xl">{counts[s] || 0}</p>
              <p className="text-white/50 text-xs uppercase tracking-widest mt-1 capitalize">{s}</p>
            </div>
          ))}
        </div>
        <button data-testid="admin-refresh" onClick={load} className="p-2.5 border border-white/15 text-white/70 hover:text-brand-orange hover:border-brand-orange transition-colors ml-4">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="quotes-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, area, service…"
            className="bg-transparent border-white/20 text-white h-11 pl-9 focus-visible:ring-brand-orange"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="quotes-filter-status" className="w-full sm:w-44 bg-transparent border-white/20 text-white h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-brand-elevated border-white/10 text-white">
            <SelectItem value="all" className="focus:bg-brand-orange focus:text-white">All statuses</SelectItem>
            {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize focus:bg-brand-orange focus:text-white">{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <button data-testid="quotes-export-csv" onClick={exportCsv} className="inline-flex items-center justify-center gap-2 px-5 h-11 border border-white/15 text-white/80 hover:text-brand-orange hover:border-brand-orange transition-colors text-sm whitespace-nowrap">
          <Download size={16} /> Export CSV
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/50 gap-3"><Loader2 className="animate-spin" /> Loading…</div>
      ) : quotes.length === 0 ? (
        <div data-testid="admin-empty" className="flex flex-col items-center justify-center py-20 text-white/40 border border-white/10 bg-brand-surface">
          <Inbox size={40} className="mb-4" />
          <p className="font-heading text-xl text-white/60">No submissions yet</p>
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="admin-no-results" className="flex flex-col items-center justify-center py-16 text-white/40 border border-white/10 bg-brand-surface">
          <Inbox size={36} className="mb-3" />
          <p className="font-heading text-lg text-white/60">No quotes match your search</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-quotes-list">
          {filtered.map((q) => (
            <div key={q.id} data-testid={`quote-row-${q.id}`} className="border border-white/10 bg-brand-surface p-6 hover:border-white/20 transition-colors">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-heading text-white text-xl">{q.name}</h3>
                    <span className={`text-xs px-3 py-1 uppercase tracking-wider ${statusColor[q.status] || statusColor.new}`}>{q.status}</span>
                    <span className="text-brand-orange text-sm font-medium">{q.service}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 mt-3 text-sm text-white/60">
                    <a href={`tel:${q.phone}`} className="hover:text-brand-orange">Tel: {q.phone}</a>
                    <a href={`mailto:${q.email}`} className="hover:text-brand-orange break-all">Email: {q.email}</a>
                    <span>Area: {q.address}</span>
                    <span>{new Date(q.created_at).toLocaleString("en-GB")}</span>
                  </div>
                  {q.message && <p className="mt-3 text-white/70 bg-black/30 p-3 border-l-2 border-brand-orange">{q.message}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={q.status} onValueChange={(v) => changeStatus(q.id, v)}>
                    <SelectTrigger data-testid={`status-select-${q.id}`} className="w-36 bg-transparent border-white/20 text-white h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-elevated border-white/10 text-white">
                      {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize focus:bg-brand-orange focus:text-white">{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <button data-testid={`delete-quote-${q.id}`} onClick={() => remove(q.id)} className="p-2.5 border border-white/15 text-white/50 hover:text-red-400 hover:border-red-400/50 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, text: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/testimonials"); setItems(data); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) { toast.error("Name and review text are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/testimonials", { ...form, rating: Number(form.rating) });
      setItems((it) => [data, ...it]);
      setForm({ name: "", location: "", rating: 5, text: "" });
      toast.success("Testimonial added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/testimonials/${id}`); setItems((it) => it.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={add} data-testid="testimonial-form" className="lg:col-span-1 border border-white/10 bg-brand-surface p-6 h-fit space-y-4">
        <h3 className="font-heading text-white text-xl mb-2">Add a testimonial</h3>
        <div className="space-y-2">
          <Label className="text-white/60 text-xs uppercase tracking-widest">Name *</Label>
          <Input data-testid="testimonial-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label className="text-white/60 text-xs uppercase tracking-widest">Location</Label>
          <Input data-testid="testimonial-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={fieldCls} placeholder="e.g. Sunderland" />
        </div>
        <div className="space-y-2">
          <Label className="text-white/60 text-xs uppercase tracking-widest">Rating</Label>
          <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: v })}>
            <SelectTrigger data-testid="testimonial-rating" className="bg-transparent border-white/20 text-white h-11"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-brand-elevated border-white/10 text-white">
              {[5, 4, 3, 2, 1].map((r) => (<SelectItem key={r} value={String(r)} className="focus:bg-brand-orange focus:text-white">{r} stars</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-white/60 text-xs uppercase tracking-widest">Review *</Label>
          <Textarea data-testid="testimonial-text" rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="bg-transparent border-white/20 text-white focus-visible:ring-brand-orange" />
        </div>
        <button type="submit" data-testid="testimonial-submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange text-white font-semibold hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add testimonial
        </button>
      </form>

      <div className="lg:col-span-2 space-y-4" data-testid="admin-testimonials-list">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-white/40 border border-white/10 bg-brand-surface">
            <Inbox size={36} className="mb-3" /> No testimonials yet
          </div>
        ) : items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="border border-white/10 bg-brand-surface p-5 flex justify-between gap-4">
            <div>
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, s) => (<Star key={s} size={14} className={s < t.rating ? "text-brand-orange fill-brand-orange" : "text-white/20"} />))}
              </div>
              <p className="text-white/80">“{t.text}”</p>
              <p className="font-heading text-white mt-2">{t.name}{t.location ? ` · ${t.location}` : ""}</p>
            </div>
            <button data-testid={`delete-testimonial-${t.id}`} onClick={() => remove(t.id)} className="self-start p-2.5 border border-white/15 text-white/50 hover:text-red-400 hover:border-red-400/50 transition-colors">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [preview, setPreview] = useState("");
  const [beforePreview, setBeforePreview] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const beforeRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/gallery"); setItems(data); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const compress = (file, setter) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    // Load via an object URL (not a base64 FileReader) to keep memory low on
    // mobile Safari — full-size iPhone photos otherwise crash the tab.
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      try {
        const max = 1280;
        let { width, height } = img;
        const scale = Math.min(1, max / Math.max(width, height));
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
        setter(dataUrl);
      } catch (err) {
        toast.error("Sorry, that photo couldn't be processed. Please try another.");
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error("Couldn't open that image. Please try a different photo (JPG or PNG works best).");
    };
    img.src = url;
  };

  const onFile = (e) => compress(e.target.files?.[0], setPreview);
  const onBeforeFile = (e) => compress(e.target.files?.[0], setBeforePreview);

  const add = async (e) => {
    e.preventDefault();
    if (!title || !preview) { toast.error("Add a title and choose a photo"); return; }
    setSaving(true);
    try {
      const payload = { title, image: preview };
      if (beforePreview) payload.before_image = beforePreview;
      const { data } = await api.post("/gallery", payload);
      setItems((it) => [data, ...it]);
      setTitle(""); setPreview(""); setBeforePreview("");
      if (fileRef.current) fileRef.current.value = "";
      if (beforeRef.current) beforeRef.current.value = "";
      toast.success("Photo added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/gallery/${id}`); setItems((it) => it.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={add} data-testid="gallery-form" className="lg:col-span-1 border border-white/10 bg-brand-surface p-6 h-fit space-y-4">
        <h3 className="font-heading text-white text-xl mb-2">Add a job photo</h3>
        <div className="space-y-2">
          <Label className="text-white/60 text-xs uppercase tracking-widest">Title / caption *</Label>
          <Input data-testid="gallery-title" value={title} onChange={(e) => setTitle(e.target.value)} className={fieldCls} placeholder="e.g. Lawn tidy-up — Durham" />
        </div>
        <div className="space-y-2">
          <Label className="text-white/60 text-xs uppercase tracking-widest">Photo (after) *</Label>
          <input ref={fileRef} data-testid="gallery-file" type="file" accept="image/*" onChange={onFile} className="block w-full text-sm text-white/60 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-brand-orange file:text-white file:font-semibold hover:file:bg-brand-orangeHover file:cursor-pointer" />
        </div>
        {preview && (
          <div className="relative border border-white/10">
            <img src={preview} alt="preview" className="w-full h-40 object-cover" />
            <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 flex items-center gap-1"><ImageIcon size={12} /> After</span>
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-white/60 text-xs uppercase tracking-widest">Before photo (optional)</Label>
          <input ref={beforeRef} data-testid="gallery-before-file" type="file" accept="image/*" onChange={onBeforeFile} className="block w-full text-sm text-white/60 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-white/10 file:text-white file:font-semibold hover:file:bg-white/20 file:cursor-pointer" />
          <p className="text-white/30 text-xs">Add a before shot to show a before/after slider on the site.</p>
        </div>
        {beforePreview && (
          <div className="relative border border-white/10">
            <img src={beforePreview} alt="before preview" className="w-full h-40 object-cover" />
            <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 flex items-center gap-1"><ImageIcon size={12} /> Before</span>
          </div>
        )}
        <button type="submit" data-testid="gallery-submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange text-white font-semibold hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Add to gallery
        </button>
      </form>

      <div className="lg:col-span-2" data-testid="admin-gallery-list">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-white/40 border border-white/10 bg-brand-surface">
            <ImageIcon size={36} className="mb-3" /> No photos yet
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {items.map((g) => (
              <div key={g.id} data-testid={`admin-gallery-${g.id}`} className="relative group border border-white/10 overflow-hidden">
                <img src={g.image} alt={g.title} className="w-full h-36 object-cover" />
                {g.before_image && <span className="absolute top-2 left-2 bg-brand-orange text-white text-[10px] uppercase tracking-wider px-2 py-0.5">Before/After</span>}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2 text-center">
                  <p className="text-white text-xs mb-2">{g.title}</p>
                  <button data-testid={`delete-gallery-${g.id}`} onClick={() => remove(g.id)} className="p-2 border border-white/30 text-white hover:text-red-400 hover:border-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsPanel() {
  const { user, applyAuth } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) { toast.error("Enter your current password to confirm changes"); return; }
    const wantsEmail = newEmail.trim() && newEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase();
    const wantsPassword = !!newPassword;
    if (!wantsEmail && !wantsPassword) { toast.error("Enter a new email or a new password"); return; }
    if (wantsPassword) {
      if (newPassword.length < 8) { toast.error("New password must be at least 8 characters"); return; }
      if (newPassword !== confirmPassword) { toast.error("New passwords do not match"); return; }
    }
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (wantsEmail) payload.new_email = newEmail.trim();
      if (wantsPassword) payload.new_password = newPassword;
      const { data } = await api.patch("/auth/credentials", payload);
      applyAuth(data);
      setCurrentPassword(""); setNewEmail(""); setNewPassword(""); setConfirmPassword("");
      toast.success("Login details updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="border border-white/10 bg-brand-surface p-6 md:p-8">
        <div className="flex items-center gap-3 mb-2">
          <KeyRound size={20} className="text-brand-orange" />
          <h3 className="font-heading text-white text-xl">Login details</h3>
        </div>
        <p className="text-white/50 text-sm mb-6">
          Change the email and password you use to sign in. You&apos;ll stay logged in after saving.
          Currently signed in as <span className="text-white/80">{user?.email}</span>.
        </p>
        <form onSubmit={submit} className="space-y-5" data-testid="settings-form">
          <div className="space-y-2">
            <Label className="text-white/60 text-xs uppercase tracking-widest">New email (optional)</Label>
            <Input data-testid="settings-new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={user?.email} className={fieldCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white/60 text-xs uppercase tracking-widest">New password (optional)</Label>
              <PasswordInput testid="settings-new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 8 characters" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs uppercase tracking-widest">Confirm new password</Label>
              <PasswordInput testid="settings-confirm-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" />
            </div>
          </div>
          <div className="space-y-2 pt-2 border-t border-white/10">
            <Label className="text-white/60 text-xs uppercase tracking-widest">Current password *</Label>
            <PasswordInput testid="settings-current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Confirm it's you" />
          </div>
          <button type="submit" data-testid="settings-submit" disabled={saving} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange text-white font-semibold hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-brand-ink">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/clarkes-logomark.png" alt="Clarkes GroundCare" className="h-8 w-auto" />
            <span className="font-heading font-semibold text-white/80 text-sm hidden sm:inline">· Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-white/50 hover:text-white text-sm transition-colors hidden sm:block">View site</Link>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-white/70 hover:text-brand-orange hover:border-brand-orange transition-colors text-sm">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <h1 className="font-heading text-white text-3xl md:text-4xl">Dashboard</h1>
          <p className="text-white/50 mt-1">Signed in as {user?.email}</p>
        </div>

        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="bg-brand-surface border border-white/10 mb-8">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="data-[state=active]:bg-brand-orange data-[state=active]:text-white text-white/60">Quote requests</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="data-[state=active]:bg-brand-orange data-[state=active]:text-white text-white/60">Testimonials</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="data-[state=active]:bg-brand-orange data-[state=active]:text-white text-white/60">Gallery</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-brand-orange data-[state=active]:text-white text-white/60">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="quotes"><QuotesPanel /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsPanel /></TabsContent>
          <TabsContent value="gallery"><GalleryPanel /></TabsContent>
          <TabsContent value="settings"><SettingsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-ink text-white/50">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  return user ? <Dashboard /> : <LoginView />;
}
